import { CommonModule } from "@angular/common";
import {
  Component,
  EventEmitter,
  inject,
  OnDestroy,
  Output,
  signal,
} from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
} from "@angular/forms";
import { PaymentChannel, StatementFilters } from "@models/transaction";
import { EventsManagementService } from "@services/events-management.service";
import { EventInfoResponseBody } from "@services/types/events-management-response.type";
import { AutoCompleteModule } from "primeng/autocomplete";
import { DatePickerModule } from "primeng/datepicker";
import { SelectModule } from "primeng/select";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from "rxjs";
import { ButtonComponent } from "../../../../shared/components/button/button.component";

interface SelectOption {
  label: string;
  value: string | null;
}

@Component({
  selector: "app-statement-filters",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DatePickerModule,
    SelectModule,
    AutoCompleteModule,
    ButtonComponent,
  ],
  templateUrl: "./statement-filters.component.html",
  styleUrl: "./statement-filters.component.scss",
})
export class StatementFiltersComponent implements OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly eventsService = inject(EventsManagementService);
  private readonly destroy$ = new Subject<void>();
  private readonly searchQuery$ = new Subject<string>();

  isLoadingEvents = false;
  isExpanded = signal(false);
  readonly today = new Date();

  toggleExpanded() {
    this.isExpanded.update((v) => !v);
  }

  @Output() filterChange = new EventEmitter<StatementFilters>();

  filterForm = this.fb.group(
    {
      startDate: [null as Date | null, [this.pastOrTodayValidator()]],
      endDate: [null as Date | null, [this.pastOrTodayValidator()]],
      paymentMethod: [null as string | null],
      paymentChannel: [null as PaymentChannel | null],
      direction: [null as string | null],
      event: [null as EventInfoResponseBody | null],
    },
    { validators: [this.endDateAfterStartDateValidator()] },
  );

  private pastOrTodayValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value as Date | null;
      if (!value) return null;
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      return value.getTime() > today.getTime()
        ? { futureDate: true }
        : null;
    };
  }

  private endDateAfterStartDateValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const start = group.get("startDate")?.value as Date | null;
      const end = group.get("endDate")?.value as Date | null;
      if (!start || !end) return null;
      return end.getTime() < start.getTime()
        ? { endBeforeStart: true }
        : null;
    };
  }

  paymentMethodOptions: SelectOption[] = [
    { label: "Todos", value: null },
    { label: "Cartão de Crédito", value: "credit_card" },
    { label: "PIX", value: "pix" },
  ];

  paymentChannelOptions: SelectOption[] = [
    { label: "Todos", value: null },
    { label: "PDV Portaria", value: "pdv_portaria" },
    { label: "PDV Yes7", value: "pdv_seller" },
    { label: "Online / Web", value: "online" },
  ];

  directionOptions: SelectOption[] = [
    { label: "Todos", value: null },
    { label: "Entradas", value: "entrada" },
    { label: "Saídas", value: "saida" },
  ];

  eventSuggestions$: Observable<EventInfoResponseBody[]> =
    this.searchQuery$.pipe(
      startWith(""),
      debounceTime(500),
      map((q) => (q ?? "").trim()),
      distinctUntilChanged(),
      tap(() => (this.isLoadingEvents = true)),
      switchMap((query) =>
        this.eventsService.getMyEvents(0, 50).pipe(
          map((response) => {
            const events = response.data ?? [];
            if (!query) return events;
            return events.filter((e) =>
              e.title.toLowerCase().includes(query.toLowerCase()),
            );
          }),
          catchError(() => of([] as EventInfoResponseBody[])),
          finalize(() => (this.isLoadingEvents = false)),
        ),
      ),
      takeUntil(this.destroy$),
    );

  handleEventSearch(event: { query: string }) {
    this.searchQuery$.next(event.query);
  }

  applyFilters() {
    this.filterForm.markAllAsTouched();

    if (this.filterForm.invalid) {
      return;
    }

    const { startDate, endDate, paymentMethod, paymentChannel, direction, event } =
      this.filterForm.value;

    const filters: StatementFilters = {
      startDate,
      endDate,
      paymentMethod,
      paymentChannel,
      direction,
      eventId: event?.id ? Number(event.id) : null,
    };

    this.filterChange.emit(filters);
  }

  clearFilters() {
    this.filterForm.reset();
    this.filterChange.emit({});
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
